import React, { CSSProperties, useEffect } from 'react';
import { RydViewport } from '../widgets/RydViewport';
import {
    getLeftNearestGridPoint,
    getRenderedWidthForHNC,
} from '../../render/renderUtils';
import { TrackColor } from '../../render/trackColor';
import { PianoRollKeybed } from '../rolls/PianoKeybed';
import {
    PianoRollConfig,
    PianoRollNoteSet
} from '../rolls/PianoRoll';
import { PianoRollCollapsed } from '../rolls/PianoRollCollapsed';
import { PianoRollCaret } from '../rolls/PianoRollCaret';
import { PianoRollInputLayer } from '../rolls/PianoRollInputLayer';
import _ from 'lodash';
import {
    HorizontalNoteContainer,
    NoteRangeType
} from '../../model/containers';
import {
    CaretController,
    ScrollController
} from '../../utils/ScrollController';
import { NoteActivityObserver } from '../../audio/NoteActivity';

import { Note } from '../../model/core';
import { HarmonicPredicates } from '../../utils/harmonyHighlight';
import { NoteSelectionGroup } from '../../utils/NoteSelectionGroup';
import { PianoRollInfoHeader } from '../rolls/PianoRollInfoHeader';
import { RydInstrument } from '../../model/instrument';
import {
    RydTrack,
    RydTrackUIState
} from '../../model/tracks';
import {
    useWorkspaceComputedContextFrame,
    useWorkspaceUIState
} from '../../stores/WorkspaceStore';
import { RydBookmarksContainer } from '../widgets/rolls/RydBookmarksContainer';
import { useZoomStore } from '../../stores/ZoomStore';
import { RydInputSource } from '../../audio/input';
import { RydProject } from '../../model/project';


export function PianoRollTrack({
    project,
    hnc,
    track,
    setTrack,
    scrollController,
    noteActivityObserver,
    uiState,
    config,
    setConfig,
    addNoteToContainer,
    readonly,
    harmonicPrecidates,
    selectionGroup,
    setSelectionGroup,
    updateHNC,
    visualSelectionActive,
    setVisualSelectionActive,
    tonalityHighlightVisible,
    setTonalityHighlightVisible,
    caretController,
    trackInstrument,
    setTrackInstrument,
    inputSource,
    setInputSource,
    setTrackUIState,
}: {
    project: RydProject,
    hnc: HorizontalNoteContainer,
    track: RydTrack,
    setTrack: (track: RydTrack) => void,
    scrollController?: ScrollController,
    noteActivityObserver?: NoteActivityObserver,
    uiState: RydTrackUIState,
    config: PianoRollConfig,
    setConfig: (config: PianoRollConfig) => void,
    addNoteToContainer: (readonly: boolean, start: number, note: Note) => void,
    readonly: boolean,
    harmonicPrecidates: HarmonicPredicates,
    selectionGroup?: NoteSelectionGroup,
    setSelectionGroup: (group: NoteSelectionGroup | undefined) => void,
    updateHNC: (hnc: HorizontalNoteContainer) => void,
    visualSelectionActive: boolean,
    setVisualSelectionActive: (active: boolean) => void,
    // FIXME: this should go to track ui state.
    tonalityHighlightVisible: boolean,
    setTonalityHighlightVisible: (visible: boolean) => void,
    caretController?: CaretController,
    trackInstrument?: RydInstrument,
    setTrackInstrument?: (instrument?: RydInstrument) => void,
    inputSource? : RydInputSource,
    setInputSource?: (source: RydInputSource) => void,
    setTrackUIState: (uiState: RydTrackUIState) => void,
}) {

    const wrappingDivRef = React.useRef<HTMLDivElement>(null);
    const [canvasWidth, setCanvasWidth] = React.useState<number>();
    const computedContextFrame = useWorkspaceComputedContextFrame();
    const workspaceUiState = useWorkspaceUIState();
    const zoomStore = useZoomStore();

    useEffect(() => {
        setCanvasWidth(wrappingDivRef.current?.parentElement?.getBoundingClientRect().width || 800);
    }, [wrappingDivRef.current]);

    const collapsedDisplayHack: CSSProperties = uiState.collapsed
        ? { height: '60px' }
        : { display: 'inline-flex' };

    const header = <PianoRollInfoHeader
        hnc={hnc}
        track={track}
        setTrack={setTrack}
        noteRange={uiState.noteRange}
        readonly={config.readonly}
        collapsed={uiState.collapsed}
        toggleSheetVisibilityCB={() => setConfig({ ...config, showSheet: !config.showSheet })}
        tonalityHighlightVisible={tonalityHighlightVisible}
        toggleTonalityHighlight={() => setTonalityHighlightVisible(!tonalityHighlightVisible)}
        updateNoteRangeCB={(noteRange: NoteRangeType) => {
            uiState.noteRange = noteRange;
            setTrackUIState(uiState);
        }}
        setCollapsed={(collapsed) => {
            uiState.collapsed = collapsed;
            setTrackUIState(uiState);
        }}
        trackColor={uiState.color}
        setTrackColor={(color: TrackColor.Color) => {
            uiState.color = color;
            setTrackUIState(uiState);
        }}
        caretController={caretController}
        updateHNCCB={updateHNC}
        trackInstrument={trackInstrument}
        setTrackInstrument={setTrackInstrument}
        inputSource={inputSource}
        setInputSource={setInputSource}
    />;

    return (<>
        <RydViewport name="Piano Roll"
            hideTitle={true}
            adaptiveHeight={true}
            scrollController={scrollController}
            sticky={header}
        >
            <div
                ref={wrappingDivRef}
                className="piano-roll-content-wrapper"
                style={{
                    ...collapsedDisplayHack,
                    width: getRenderedWidthForHNC({ notes: hnc, includeKeyboard: true })
                }} >

                {uiState.collapsed && <RydBookmarksContainer
                    project={project}
                    bookmarks={track.bookmarks}
                    zoomFactor={zoomStore.zoomFactor}
                    canvasWidth={canvasWidth || 0}
                    showLoops={false}
                />}

                {!uiState.collapsed && <>
                    <PianoRollKeybed
                        track={track}
                        noteRange={uiState.noteRange}
                        tonality={computedContextFrame.tonality}
                        noteActivityObserver={noteActivityObserver}
                    />
                    <PianoRollInputLayer
                        trackId={track.id}
                        hnc={hnc}
                        timeSignature={computedContextFrame.timeSignature}
                        noteRange={uiState.noteRange}
                        addNoteToContainer={_.curry(addNoteToContainer)(readonly)}
                        harmonicPredicates={harmonicPrecidates}
                        scrollController={scrollController}
                        setHNC={updateHNC}
                    />
                    <RydBookmarksContainer
                        project={project}
                        bookmarks={track.bookmarks}
                        zoomFactor={zoomStore.zoomFactor}
                        canvasWidth={canvasWidth || 0}
                        showLoops={false}
                    />
                    <PianoRollNoteSet
                        hnc={hnc}
                        trackId={track.id}
                        noteRange={uiState.noteRange}
                        readonly={config.readonly}
                        updateHNCCB={updateHNC}
                        scrollController={scrollController}
                        trackColor={uiState.color}
                        normalizeDropPosition={(x: number, zoomFactor: number) => {
                            if (!workspaceUiState.snapToGrid) return x;
                            return getLeftNearestGridPoint(
                                x,
                                hnc.getPPQ(),
                                zoomFactor,
                                workspaceUiState.gridResolution,
                                computedContextFrame.timeSignature);
                        }}
                    />
                </>}
                {uiState.collapsed && canvasWidth && <PianoRollCollapsed
                    hnc={hnc}
                    noteColor={uiState.color}
                    scrollController={scrollController}
                    getCanvasWidth={() => canvasWidth}
                />}

                {caretController && <PianoRollCaret caretController={caretController} />}

            </div>
        </RydViewport>
    </>);
}